import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Stack,
  useTheme,
  alpha,
  Alert,
  CircularProgress
} from '@mui/material';
import { Close, Add, Edit, Delete, Comment, CalendarToday } from '@mui/icons-material';
import { Lead, Observacao } from '../types';
import { addLeadObservacao, updateLeadObservacao, deleteLeadObservacao, getLeadObservacoes } from '../utils/api';
import { useTranslation } from 'react-i18next';

interface ObservacoesModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead | null;
  onLeadUpdate: (updatedLead: Lead) => void;
}

const ObservacoesModal: React.FC<ObservacoesModalProps> = ({ open, onClose, lead, onLeadUpdate }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [newComentario, setNewComentario] = useState('');
  const [editingObservacao, setEditingObservacao] = useState<Observacao | null>(null);
  const [editComentario, setEditComentario] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [observacoes, setObservacoes] = useState<Observacao[]>([]);

  const observacoesCountLabel = (count: number) =>
    t('dashboard.observations.observationsCount', { count });

  useEffect(() => {
    if (!open || !lead) return;

    let isActive = true;

    setNewComentario('');
    setEditingObservacao(null);
    setEditComentario('');
    setError(null);
    setSuccess(null);
    setObservacoes(lead.observacoes || []);

    getLeadObservacoes(lead._id.$oid)
      .then((obs) => {
        if (!isActive) return;
        setObservacoes(obs);
        // Do not call onLeadUpdate here to avoid parent re-renders loop
      })
      .catch(() => {
        // ignore fetch error here; keep local state
      });

    return () => {
      isActive = false;
    };
  }, [open, lead]);

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return t('dashboard.observations.invalidDate');
    }
  };

  const handleAddObservacao = async () => {
    if (!lead || !newComentario.trim()) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const created = await addLeadObservacao(lead._id.$oid, newComentario.trim());
      const nextObservacoes = [...observacoes, created];
      setObservacoes(nextObservacoes);
      onLeadUpdate({ ...lead, observacoes: nextObservacoes });
      setNewComentario('');
      setSuccess(t('dashboard.observations.addedSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.observations.addError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditObservacao = async () => {
    if (!lead || !editingObservacao || !editComentario.trim()) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await updateLeadObservacao(lead._id.$oid, editingObservacao._id, editComentario.trim());
      const updatedObservacoes = observacoes.map((obs) =>
        obs._id === editingObservacao._id ? { ...obs, comentario: editComentario.trim() } : obs
      );
      setObservacoes(updatedObservacoes);
      onLeadUpdate({ ...lead, observacoes: updatedObservacoes });
      setEditingObservacao(null);
      setEditComentario('');
      setSuccess(t('dashboard.observations.editedSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.observations.editError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteObservacao = async (observacaoId: string) => {
    if (!lead) return;

    if (!window.confirm(t('dashboard.observations.deleteConfirm'))) return;

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await deleteLeadObservacao(lead._id.$oid, observacaoId);
      const updatedObservacoes = observacoes.filter(obs => obs._id !== observacaoId);
      setObservacoes(updatedObservacoes);
      onLeadUpdate({ ...lead, observacoes: updatedObservacoes });
      setSuccess(t('dashboard.observations.removedSuccess'));
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('dashboard.observations.removeError'));
    } finally {
      setIsLoading(false);
    }
  };

  const startEditing = (observacao: Observacao) => {
    setEditingObservacao(observacao);
    setEditComentario(observacao.comentario);
  };

  const cancelEditing = () => {
    setEditingObservacao(null);
    setEditComentario('');
  };

  if (!lead) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          backgroundImage: 'none'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Comment color="primary" />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {t('dashboard.observations.title')}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(theme.palette.action.hover, 0.08)
            }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, mt: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ color: 'primary.main', fontWeight: 600 }}
          >
            {lead.name}
          </Typography>
        </Box>

        {/* Alert Messages */}
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        {/* Add New Observação */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(theme.palette.primary.main, 0.02)
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 600, color: 'primary.main' }}
          >
            {t('dashboard.observations.addNew')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
              >
                {t('dashboard.observations.comment')}
              </Typography>
              <textarea
                value={newComentario}
                onChange={(e) => setNewComentario(e.target.value)}
                placeholder={t('dashboard.observations.commentPlaceholder')}
                disabled={isLoading}
                style={{
                  width: '100%',
                  minHeight: '120px',
                  padding: '12px',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: '4px',
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '14px',
                  lineHeight: '1.5',
                  resize: 'vertical',
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = theme.palette.primary.main;
                  e.target.style.borderWidth = '2px';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = theme.palette.divider;
                  e.target.style.borderWidth = '1px';
                }}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={isLoading ? <CircularProgress size={16} /> : <Add />}
              onClick={handleAddObservacao}
              disabled={!newComentario.trim() || isLoading}
              sx={{ minWidth: 120, color: theme.palette.mode === 'dark' ? '#000' : undefined }}
            >
              {isLoading ? t('dashboard.observations.adding') : t('dashboard.observations.add')}
            </Button>
          </Box>
        </Paper>

        {/* List of Observações */}
        <Box>
          <Typography
            variant="h6"
            gutterBottom
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {observacoesCountLabel(observacoes.length)}
          </Typography>

          {observacoes.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: alpha(theme.palette.background.default, 0.5)
              }}
            >
              <Comment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography
                variant="body1"
                color="text.secondary"
              >
                {t('dashboard.observations.noObservations')}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
              >
                {t('dashboard.observations.addFirst')}
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={2}>
              {observacoes.map((observacao) => (
                <Paper
                  key={observacao._id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      backgroundColor: alpha(theme.palette.primary.main, 0.02)
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {editingObservacao?._id === observacao._id ? (
                    // Edit Mode
                    <Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          gutterBottom
                        >
                          {t('dashboard.observations.editComment')}
                        </Typography>
                        <textarea
                          value={editComentario}
                          onChange={(e) => setEditComentario(e.target.value)}
                          style={{
                            width: '100%',
                            minHeight: '120px',
                            padding: '12px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '4px',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: '14px',
                            lineHeight: '1.5',
                            resize: 'vertical',
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = theme.palette.primary.main;
                            e.target.style.borderWidth = '2px';
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = theme.palette.divider;
                            e.target.style.borderWidth = '1px';
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={cancelEditing}
                          disabled={isLoading}
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleEditObservacao}
                          disabled={!editComentario.trim() || isLoading}
                          startIcon={isLoading ? <CircularProgress size={14} /> : <Edit />}
                        >
                          {isLoading ? t('dashboard.observations.saving') : t('dashboard.observations.save')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    // View Mode
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          mb: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {formatDate(observacao.dataContato)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => startEditing(observacao)}
                            disabled={isLoading}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: 'primary.main' }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteObservacao(observacao._id)}
                            disabled={isLoading}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': { color: 'error.main' }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.6, whiteSpace: 'pre-line' }}
                      >
                        {observacao.comentario}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          pt: 2,
          borderTop: `1px solid ${theme.palette.divider}`
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            px: 3
          }}
        >
          {t('dashboard.leadDetails.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ObservacoesModal;
